// Shared field-grouping for a claim's data -- used by BOTH the on-screen
// "review before you submit" preview (InsuranceClaimsTab.tsx) and the PDF
// export (generateClaimPdf.ts), so what Janelle reads on screen before
// clicking Submit is exactly what's in the PDF, not a separate JSON blob
// that can drift out of sync with it.
import { supabase } from "@/lib/supabase";
import {
  InsuranceIncident,
  InsuranceClaim,
  IncidentThirdParty,
  ClaimType,
  fetchIncidentThirdParties,
} from "@/lib/incidentReports";
import { loadInsuranceBrokerSettings } from "@/lib/insuranceBrokerSettings";
import { formatDate } from "@/lib/dates";

export const CLAIM_TYPE_LABEL: Record<ClaimType, string> = {
  full: "Full Claim",
  section2: "Section 2",
  third_party_at_fault: "Third Party At Fault",
};

export interface ClaimDriverRow {
  driver_name: string;
  phone: string | null;
  email: string | null;
  address: string | null;
  dl_number: string | null;
  dl_version: number | null;
  dl_expiry: string | null;
  dl_class: string | null;
  dl_issue_date: string | null;
  dl_conditions: string | null;
  convictions_last_7_years: string | null;
  accidents_claims_last_5_years: string | null;
}

export interface ClaimVehicleRow {
  vehicle_number: string;
  vehicle_type: string | null;
  registration: string | null;
  make: string | null;
  model: string | null;
  year: number | null;
  seat_capacity: number | null;
  insurance_body_type: string | null;
  insurance_vehicle_class: string | null;
  modification_details: string | null;
  cof_date: string | null;
  finance_details: string | null;
  other_insurance_details: string | null;
}

export type ClaimSectionRow = [string, string];
export interface ClaimSection {
  title: string;
  rows: ClaimSectionRow[];
}

const yesNo = (v: boolean) => (v ? "Yes" : "No");
const yesNoDetails = (details: string | null) => (details && details.trim() ? `Yes -- ${details}` : "No");
const dash = (v: string | number | null | undefined) => (v === null || v === undefined || v === "" ? "--" : String(v));
/** Same as dash(), but runs the value through the app's configured
 *  date-format + timezone instead of showing the raw YYYY-MM-DD from the DB. */
const dashDate = (v: string | null | undefined) => (v ? formatDate(v) : "--");

/** Taxis are commercial vehicles and run on a COF (Certificate of Fitness),
 *  not a WOF (Warrant of Fitness -- that's for personal vehicles). Whether
 *  the vehicle was compliant at the time of the incident is derivable
 *  straight from its COF expiry date rather than a separately hand-ticked
 *  checkbox that could silently go stale, so this compares incident_date
 *  against cof_date instead of trusting a stored boolean. Comparison stays
 *  on the raw YYYY-MM-DD strings (they sort correctly lexicographically);
 *  only the displayed dates go through formatDate(). */
const cofCurrentAtIncident = (cofDate: string | null, incidentDate: string): string => {
  if (!cofDate) return "Unknown -- no COF date on file";
  return cofDate >= incidentDate
    ? `Yes -- COF expires ${formatDate(cofDate)}`
    : `No -- COF expired ${formatDate(cofDate)}`;
};

export interface ClaimData {
  incident: InsuranceIncident;
  claim: InsuranceClaim;
  driver: ClaimDriverRow | null;
  vehicle: ClaimVehicleRow | null;
  thirdParties: IncidentThirdParty[];
  broker: Awaited<ReturnType<typeof loadInsuranceBrokerSettings>>;
}

export async function loadClaimData(incident: InsuranceIncident, claim: InsuranceClaim): Promise<ClaimData> {
  const [{ data: driver }, { data: vehicle }, thirdParties, broker] = await Promise.all([
    supabase
      .from("drivers")
      .select(
        "driver_name, phone, email, address, dl_number, dl_version, dl_expiry, dl_class, dl_issue_date, dl_conditions, convictions_last_7_years, accidents_claims_last_5_years"
      )
      .eq("driver_name", incident.driver_name)
      .maybeSingle<ClaimDriverRow>(),
    supabase
      .from("vehicles")
      .select(
        "vehicle_number, vehicle_type, registration, make, model, year, seat_capacity, insurance_body_type, insurance_vehicle_class, modification_details, cof_date, finance_details, other_insurance_details"
      )
      .eq("vehicle_number", incident.vehicle_number)
      .maybeSingle<ClaimVehicleRow>(),
    fetchIncidentThirdParties(incident.id),
    loadInsuranceBrokerSettings(),
  ]);
  return { incident, claim, driver: driver ?? null, vehicle: vehicle ?? null, thirdParties, broker };
}

export function buildClaimSections(data: ClaimData): ClaimSection[] {
  const { incident, claim, driver, vehicle, thirdParties, broker } = data;
  const sections: ClaimSection[] = [];

  sections.push({
    title: "Make a Claim",
    rows: [
      ["Claim Type", CLAIM_TYPE_LABEL[claim.claim_type]],
      ["Branch", dash(broker.branch)],
      ["Your Broker", dash(broker.brokerName)],
      ["Client Number", dash(broker.clientNumber)],
      ["Client Type", dash(broker.clientType)],
      ["Broker Reference", dash(claim.broker_reference)],
      ["Claim Number", dash(claim.claim_number)],
    ],
  });

  sections.push({
    title: "Policy / Client Details",
    rows: [
      ["Company Name", dash(broker.companyName)],
      ["Company Number", dash(broker.companyNumber)],
    ],
  });

  sections.push({
    title: "Address",
    rows: [
      ["Address 1", dash(broker.address1)],
      ["Address 2", dash(broker.address2)],
      ["City", dash(broker.city)],
      ["Postal Code", dash(broker.postalCode)],
    ],
  });

  sections.push({
    title: "Preferred Contact Details",
    rows: [
      ["Contact Name", dash(broker.contactName)],
      ["Relationship", dash(broker.contactRelationship)],
      ["Contact Number", dash(broker.contactNumber)],
      ["Email", dash(broker.contactEmail)],
      ["Rothbury App?", dash(broker.hasRothburyApp)],
    ],
  });

  sections.push({
    title: "Vehicle Details",
    rows: [
      ["Vehicle #", dash(vehicle?.vehicle_number ?? incident.vehicle_number)],
      ["Registration", dash(vehicle?.registration)],
      ["Make / Model", `${dash(vehicle?.make)} ${dash(vehicle?.model)}`.trim()],
      ["Year", dash(vehicle?.year)],
      ["Body Type", dash(vehicle?.insurance_body_type)],
      ["NZTA Vehicle Class", dash(vehicle?.insurance_vehicle_class)],
      ["Seats", dash(vehicle?.seat_capacity)],
      ["COF Current at Incident", vehicle ? cofCurrentAtIncident(vehicle.cof_date, incident.incident_date) : "--"],
      ["Modifications", yesNoDetails(vehicle?.modification_details ?? null)],
      ["Finance", yesNoDetails(vehicle?.finance_details ?? null)],
      ["Other Insurance", yesNoDetails(vehicle?.other_insurance_details ?? null)],
    ],
  });

  sections.push({
    title: "Driver Details",
    rows: [
      ["Name", dash(driver?.driver_name ?? incident.driver_name)],
      ["Phone", dash(driver?.phone)],
      ["Email", dash(driver?.email)],
      ["Address", dash(driver?.address)],
      ["Licence Number", dash(driver?.dl_number)],
      ["Licence Version", dash(driver?.dl_version)],
      ["Licence Class", dash(driver?.dl_class)],
      ["Licence Issue Date", dashDate(driver?.dl_issue_date)],
      ["Licence Expiry", dashDate(driver?.dl_expiry)],
      ["Licence Conditions", yesNoDetails(driver?.dl_conditions ?? null)],
      ["Convictions (Last 7 Years)", yesNoDetails(driver?.convictions_last_7_years ?? null)],
      ["Accidents/Claims (Last 5 Years)", yesNoDetails(driver?.accidents_claims_last_5_years ?? null)],
    ],
  });

  sections.push({
    title: "Incident Details",
    rows: [
      ["Date", dashDate(incident.incident_date)],
      ["Time", dash(incident.incident_time)],
      ["Location", dash(incident.location_description)],
      ["Weather", dash(incident.weather_conditions)],
      ["Road Conditions", dash(incident.road_conditions)],
      ["Speed Limit", dash(incident.speed_limit)],
      ["Vehicle Speed", dash(incident.vehicle_speed)],
      ["What Happened", dash(incident.narrative)],
      ["Damage to Our Vehicle", dash(incident.own_vehicle_damage_description)],
      ["Vehicle's Current Location", dash(incident.vehicle_location_now)],
      ["Police Involved", incident.police_involved ? `Yes -- ${dash(incident.police_incident_number)}` : "No"],
      ["Witnesses", incident.witnesses_present ? `Yes -- ${dash(incident.witness_details)}` : "No"],
      ["Injuries", incident.injuries ? `Yes -- ${dash(incident.injury_details)}` : "No"],
      ["Alcohol/Drugs Involved", yesNo(incident.alcohol_drugs_involved)],
    ],
  });

  thirdParties.forEach((p: IncidentThirdParty, idx) => {
    sections.push({
      title: `Other Party #${idx + 1}`,
      rows: [
        ["Name", dash(p.other_driver_name)],
        ["Phone", dash(p.other_driver_phone)],
        ["Licence Number", dash(p.other_driver_licence)],
        ["Vehicle Rego", dash(p.other_vehicle_rego)],
        ["Vehicle Make/Model", `${dash(p.other_vehicle_make)} ${dash(p.other_vehicle_model)}`.trim()],
        ["Owner Name", dash(p.other_owner_name)],
        ["Owner Phone", dash(p.other_owner_phone)],
        ["Their Insurer", dash(p.other_insurer_name)],
        ["Their Claim/Policy #", dash(p.other_claim_policy_number)],
        ["Damage to Their Vehicle", dash(p.other_vehicle_damage_description)],
        ["Property Description", dash(p.property_description)],
      ],
    });
  });

  sections.push({
    title: "Repairer & Repair Cost",
    rows: [
      ["Repairer Name", dash(incident.repairer_name)],
      ["Repairer Address", dash(incident.repairer_address)],
      ["Repairer Phone", dash(incident.repairer_phone)],
      ["Repairer Email", dash(incident.repairer_email)],
      ["Repair Estimate", incident.repair_estimate != null ? `$${incident.repair_estimate.toFixed(2)}` : "--"],
      ["Repair Cost (Actual)", incident.repair_cost != null ? `$${incident.repair_cost.toFixed(2)}` : "--"],
    ],
  });

  sections.push({
    title: "Direct Credit Authority",
    rows: [
      ["Bank Account Name", dash(broker.bankAccountName)],
      ["Bank Account Number", dash(broker.bankAccountNumber)],
    ],
  });

  return sections;
}
